import { stepName, type Standing, type TileLine, type TileStep } from './model.ts';

/**
 * How a step looks for where it stands (design study 12, states A), read once by every drawing of it: the tile, the
 * phone's row and the key under the home's sentence. Done in moss with its tick, under way ringed in cedar, waiting on
 * the client (or an inspection not passed) ringed in bengara, not started greyed; offered, dashed; a deviation on a job
 * (come late, or a gate marked done early: design study 21) ringed dashed in bengara; not configured, drawn as any step
 * is, its picture and all, ringed in the warning ochre with "Not configured" at its foot (Jason, 2026-10-07: "clearly
 * highlight unconfigured steps in any tree and always show its icon"). Colour is never the only signal: the foot says it
 * in words.
 */

/** The words at a step's foot, coloured by where it stands. */
export const FOOT: Record<Standing, string> = {
  done: 'text-moss',
  now: 'text-cedar-deep',
  next: 'text-ink',
  yours: 'font-medium text-alert',
  ahead: 'text-ink-3',
  override: 'text-slate',
  failed: 'font-medium text-alert',
};

/** Where it stands, in a word, for a key. */
export const STANDING_WORDS: Record<Standing, string> = {
  done: 'Done',
  now: 'Under way',
  next: 'Next',
  yours: 'Waiting on you',
  ahead: 'Not started yet',
  override: 'Done on an override',
  failed: 'Did not pass',
};

/** A deviation's words at a foot: in bengara, whatever the standing. */
export const DEVIATION_FOOT = 'text-alert';

/** A step not configured, in words at its foot (Jason, 2026-10-07). */
export const NOT_CONFIGURED = 'Not configured';

/**
 * Not configured, its words: the warning ochre deepened (AA on paper and sheet), the ring's own hue, a meaning no other
 * ring or foot of a tree has (the master's red and blue, the job's cedar, bengara and moss, ink found or opened).
 */
export const NOT_CONFIGURED_FOOT = 'text-ochre-deep';

/**
 * What a tile says on hover: its name, then its foot where the tile cuts it short or it is a deviation, and "not
 * configured"; held (L-17 B), what holds it, named as its panel says it ("Stakeout. Held: Construction Authorized, two
 * steps back, is not done yet: it waits on the client."), as a phone's row says it on hover too.
 */
export function tileTitle(s: Pick<TileStep, 'title' | 'foot' | 'hold'>, withFoot: boolean, unconfigured: boolean): string {
  const head = [stepName(s.title), withFoot && s.foot ? s.foot : null, unconfigured ? NOT_CONFIGURED.toLowerCase() : null].filter(Boolean).join(': ');
  return s.hold ? `${head}. ${s.hold}` : head;
}

type Look = Pick<TileStep, 'ghost' | 'configured' | 'standing' | 'highlight' | 'full' | 'deviation'>;

/** Drawn pale: not on the job, or not started. A step not configured is drawn as any step is (and marked: `ringOf`, `footOf`). */
export const isPale = (s: Look): boolean => !s.full && (Boolean(s.ghost) || s.standing === 'ahead');

/**
 * Its band (who does it) drawn faint, as on a step drawn pale, and on any step only offered, drawn full or not (P-16,
 * Jason, 2026-10-09: "all the nsb headings or whatever is the owner of a node should be dimmed similarly when the node is
 * pending, so not black in this case but faint grey"): a step not on the job yet never carries a full black band. Its
 * band's word still says whose it is.
 */
export const bandFaint = (s: Look): string => (s.ghost ? 'opacity-35' : isPale(s) ? 'opacity-40' : '');

/** Its frame: dashed while not on the job, light while not started, else a sheet with a strong rule. */
export function frameOf(s: Look): string {
  if (s.ghost) return 'border border-dashed border-rule-strong bg-transparent';
  if (s.standing === 'ahead') return 'border border-rule bg-paper';
  return 'border border-rule-strong bg-sheet';
}

const RINGS = {
  2: {
    ink: 'outline outline-2 outline-offset-2 outline-ink',
    cedar: 'outline outline-2 outline-offset-2 outline-cedar',
    alert: 'outline outline-2 outline-offset-2 outline-alert',
    deviation: 'outline outline-2 outline-dashed outline-offset-2 outline-alert',
    unconfigured: 'outline outline-2 outline-offset-2 outline-warning',
  },
  1: {
    ink: 'outline outline-2 outline-offset-1 outline-ink',
    cedar: 'outline outline-2 outline-offset-1 outline-cedar',
    alert: 'outline outline-2 outline-offset-1 outline-alert',
    deviation: 'outline outline-2 outline-dashed outline-offset-1 outline-alert',
    unconfigured: 'outline outline-2 outline-offset-1 outline-warning',
  },
} as const;

/**
 * The ring round it: nothing comes in (red) or it leads to nothing (blue) on the master, found or opened in ink, a
 * deviation (come late, or a gate marked done early) dashed in bengara, under way in cedar, waiting on the client or not
 * passed in bengara, and otherwise, not configured, in the warning ochre. Where a ring of the job's standing is drawn, a
 * step not configured keeps its words at its foot (`footOf`).
 */
export function ringOf(s: Look, selected = false, offset: 1 | 2 = 2): string {
  if (s.highlight === 'loose') return 'outline outline-[3px] outline-offset-2 outline-[#e00000]';
  if (s.highlight === 'dead') return 'outline outline-[3px] outline-offset-2 outline-[#0057ff]';
  const standing = s.ghost ? null : (s.standing ?? null);
  if (selected) return RINGS[offset].ink;
  if (s.deviation) return RINGS[offset].deviation;
  if (standing === 'now') return RINGS[offset].cedar;
  if (standing === 'yours' || standing === 'failed') return RINGS[offset].alert;
  if (s.configured === false && !s.ghost) return RINGS[offset].unconfigured;
  return '';
}

/** What a tile's foot holds on one line, in its mono capitals; a done step's tick before it takes two of them. */
const FOOT_ROOM = 21;
const TICK_ROOM = 2;

/** Whether words fit on a line of a step's foot, with the tick a done step's first line carries. */
const fits = (words: string, ticked: boolean): boolean => words.length <= FOOT_ROOM - (ticked ? TICK_ROOM : 0);

/**
 * A foot too long for its line breaks before its day, on two (Jason, 2026-10-07, a gate approved: "Approved by Robin"
 * over "Oct 9"), so nothing is cut short; one that fits stays one line. `ticked`: a done step's, its tick before it.
 */
export function footLines(foot: string, ticked = false): [string, string | null] {
  const at = foot.lastIndexOf(' · ');
  return !fits(foot, ticked) && at > 0 ? [foot.slice(0, at), foot.slice(at + 3)] : [foot, null];
}

/**
 * A foot kept to the one line it has, where "Not configured" takes the second (L-25e; Jason, 2026-10-08, yes to
 * "Approved · Oct 8" over "Not configured"): the day kept, and who left to the step's panel, where a done gate already
 * says who marked it ("Approved by Jason · Oct 8" reads "Approved · Oct 8"); a done step's still too long (a day from
 * another year, L-25a), the day alone, its tick saying it is done. A foot with no tick keeps its first word whatever
 * comes after it ("Late · waiting on the client", "Under way · Nov 30, 2008"), cut short at the line's end rather than
 * lose where it stands. The whole of it is the tile's title and its words.
 */
export function footOneLine(foot: string, ticked = false): string {
  const at = foot.lastIndexOf(' · ');
  if (fits(foot, ticked) || at < 0) return foot;
  const head = foot.slice(0, at);
  const day = foot.slice(at + 3);
  const by = head.indexOf(' by ');
  const what = by > 0 ? head.slice(0, by) : head;
  if (fits(`${what} · ${day}`, ticked)) return `${what} · ${day}`;
  return ticked ? day : foot;
}

/** A line of words at a step's foot: where it stands (`standing`, coloured by it), or that it is not configured. */
export interface FootLine {
  text: string;
  tone: 'standing' | 'unconfigured';
}

/**
 * The words at a step's foot, a line or two, for the tile: where it stands (a foot too long broken before its day,
 * `footLines`), and, for a step not configured, "Not configured" on a line of its own under it (the standing then kept
 * to its one line, its day kept, `footOneLine`), or alone where nothing else is at its foot (the master; a job being
 * set up).
 */
export function footOf(s: Pick<TileStep, 'foot' | 'configured' | 'ghost' | 'standing'>): FootLine[] {
  const unconfigured = s.configured === false && !s.ghost;
  const ticked = s.standing === 'done' && !s.ghost;
  const lines: FootLine[] = [];
  if (s.foot) {
    const [a, b] = footLines(s.foot, ticked);
    if (unconfigured) lines.push({ text: footOneLine(s.foot, ticked), tone: 'standing' });
    else if (!b) lines.push({ text: a, tone: 'standing' });
    else lines.push({ text: a, tone: 'standing' }, { text: b, tone: 'standing' });
  }
  if (unconfigured) lines.push({ text: NOT_CONFIGURED, tone: 'unconfigured' });
  return lines;
}

/**
 * Whether a step left out leads on to a step of the job, through any dots after it: then the line into it is drawn
 * whole (design study 18), as the way on from it is.
 */
export function leadsOn(steps: readonly Pick<TileStep, 'key' | 'needs' | 'dot' | 'ghost'>[], from: string): boolean {
  const walk = (key: string, seen: ReadonlySet<string>): boolean =>
    steps.some((s) => s.needs.includes(key) && !seen.has(s.key) && (s.dot ? walk(s.key, new Set([...seen, key])) : !s.ghost));
  return walk(from, new Set());
}

/**
 * How a line on the board is drawn: dashed while it runs to or from a step not on the job, a line into a dot as the way
 * on from it is; and a late step's line back to an earlier phase in bengara, the one line that crosses
 * a transition (study 21: "A late step’s lines are the only ones that cross a transition, drawn in bengara").
 */
export function lineLook(
  l: Pick<TileLine, 'src' | 'dst' | 'late'>,
  byKey: ReadonlyMap<string, Pick<TileStep, 'key' | 'ghost' | 'dot'>>,
  leads: (key: string) => boolean,
): { dashed: boolean; late: boolean } {
  const src = byKey.get(l.src);
  const dst = byKey.get(l.dst);
  const dashed = dst?.dot ? !leads(dst.key) : Boolean(dst?.ghost || (src?.ghost && !src.dot));
  return { dashed, late: Boolean(l.late) };
}
